import { useState } from 'react';
import { DateTime } from 'luxon';
import { useHistory } from 'react-router-dom';
import { Item } from '@baemingo/shopping-cart-events/dist/types';
import { useBridgeApi, useDispatch, useSelector } from './index';
import * as actions from '../actions';
import { useShoppingCart, useShoppingCartApi } from '../Components/ShoppingCartUniverse';
import { useStorage } from '../Components/Storage';

export function useMenuExpired() {
  const { push } = useHistory();
  const api = useBridgeApi();
  const shoppingCart = useShoppingCart();
  const shoppingCartApi = useShoppingCartApi();
  const dispatch = useDispatch();
  const [, updateStorage] = useStorage();
  const [isMenuExpired, setIsMenuExpired] = useState(false);
  const [expiredItems, setExpiredItems] = useState<Item[]>([]);
  const menu = useSelector((state) => state.menu);

  // Check if the menu has expired
  const checkMenuExpired = () => {
    if (!menu) {
      return false;
    }

    const timeToCheckTowards = DateTime.local(); // Get current local date and time
    const currentDayOfWeek = timeToCheckTowards.toFormat('cccc').toUpperCase(); // Get current day of the week in uppercase
    const availability = menu.availabilities.find((a) => a.dayOfWeek === currentDayOfWeek);

    if (!availability) {
      return false;
    }

    const { timeRange } = availability;
    const startTime = DateTime.fromFormat(timeRange.start, 'HH:mm');
    let endTime;

    if (availability.timeRange.end === '00:00') {
      // We cannot compare 00:00 directly since it is the start of the day, so we need to convert it to 23:59:59:999
      endTime = DateTime.fromFormat('23:59:59:999', 'HH:mm:ss:SSS');
    } else {
      endTime = DateTime.fromFormat(timeRange.end, 'HH:mm');
    }

    if (timeToCheckTowards >= startTime && timeToCheckTowards < endTime) {
      return false;
    }

    setIsMenuExpired(true);
    return true;
  };

  // Find items that are no longer in the menu
  const findExpiredItems = () => {
    if (!menu) {
      return [];
    }

    const items: Item[] = [];

    shoppingCart.items.forEach((item) => {
      const menuEntry = menu.menu.menuEntries.find((e) => item.entryId === e.id);
      if (!menuEntry) {
        items.push(item);
      }
    });

    return items;
  };

  // Check the menu then items
  const checkCartMenuExpired = () => {
    const menuExpired = checkMenuExpired();

    if (menuExpired) {
      return true;
    }

    const items = findExpiredItems();

    if (items.length > 0) {
      setExpiredItems(items);
      return true;
    }

    return false;
  };

  // Clear cart
  const clearCart = () => {
    api.vibrate('impactLight');
    shoppingCartApi.clearPromoCode();
    shoppingCartApi.clearCart();
    dispatch(actions.setCartId(null));
    updateStorage({ cartId: null });
  };

  // Callback to close item modal (menu is expired)
  const onConfirmMenuExpired = () => {
    clearCart();
    setIsMenuExpired(false);
    push('/');
  };

  // Callback to close cart modal (menu or items expired)
  const onConfirmCartMenuExpired = () => {
    if (isMenuExpired) {
      onConfirmMenuExpired();
      return;
    }

    expiredItems.forEach((item) => {
      shoppingCartApi.removeItem(item.id);
    });

    setExpiredItems([]);
  };

  return {
    checkMenuExpired,
    checkCartMenuExpired,
    isMenuExpired,
    isItemsExpired: expiredItems.length > 0,
    onConfirmMenuExpired,
    onConfirmCartMenuExpired,
    expiredItems,
  };
}
